/**
 * Redimensiona e comprime uma imagem para reduzir o tamanho do arquivo
 * @param {Blob} imageBlob - Blob da imagem original
 * @param {number} maxWidth - Largura máxima desejada (padrão: 1920)
 * @param {number} maxHeight - Altura máxima desejada (padrão: 1080)
 * @param {number} quality - Qualidade JPEG (0.0 a 1.0, padrão: 0.85)
 * @param {number} maxSizeKB - Tamanho máximo desejado em KB (padrão: 500KB)
 * @returns {Promise<Blob>} - Blob da imagem comprimida
 */
export async function compressImage(
  imageBlob,
  maxWidth = 1920,
  maxHeight = 1080,
  quality = 0.85,
  maxSizeKB = 500
) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(imageBlob);

    img.onload = () => {
      URL.revokeObjectURL(url);

      // Calcular novas dimensões mantendo proporção
      let width = img.width;
      let height = img.height;

      if (width > maxWidth || height > maxHeight) {
        const ratio = Math.min(maxWidth / width, maxHeight / height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }

      // Criar canvas com dimensões redimensionadas
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d');
      
      // Melhorar qualidade de renderização
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      
      // Desenhar imagem redimensionada
      ctx.drawImage(img, 0, 0, width, height);

      // Função para tentar comprimir com qualidade específica
      const tryCompress = (currentQuality) => {
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Falha ao comprimir imagem'));
              return;
            }

            const sizeKB = blob.size / 1024;
            console.log(`[ImageCompression] Imagem comprimida: ${sizeKB.toFixed(2)}KB (qualidade: ${currentQuality.toFixed(2)})`);

            // Se o tamanho estiver OK ou qualidade já estiver muito baixa, retornar
            if (sizeKB <= maxSizeKB || currentQuality <= 0.5) {
              resolve(blob);
            } else {
              // Tentar com qualidade menor (reduzir 0.1 por vez até 0.5)
              const newQuality = Math.max(0.5, currentQuality - 0.1);
              console.log(`[ImageCompression] Tamanho ainda grande (${sizeKB.toFixed(2)}KB), tentando qualidade ${newQuality.toFixed(2)}`);
              tryCompress(newQuality);
            }
          },
          'image/jpeg',
          currentQuality
        );
      };

      // Iniciar compressão com qualidade inicial
      tryCompress(quality);
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Erro ao carregar imagem para compressão'));
    };

    img.src = url;
  });
}

/**
 * Redimensiona e comprime uma imagem a partir de um canvas
 * Útil quando já temos um canvas (ex: captura de câmera)
 * @param {HTMLCanvasElement} canvas - Canvas com a imagem
 * @param {number} maxWidth - Largura máxima desejada (padrão: 1920)
 * @param {number} maxHeight - Altura máxima desejada (padrão: 1080)
 * @param {number} quality - Qualidade JPEG inicial (0.0 a 1.0, padrão: 0.85)
 * @param {number} maxSizeKB - Tamanho máximo desejado em KB (padrão: 500KB)
 * @returns {Promise<Blob>} - Blob da imagem comprimida
 */
export async function compressImageFromCanvas(
  canvas,
  maxWidth = 1920,
  maxHeight = 1080,
  quality = 0.85,
  maxSizeKB = 500
) {
  return new Promise((resolve, reject) => {
    // Calcular novas dimensões mantendo proporção
    let width = canvas.width;
    let height = canvas.height;

    if (width > maxWidth || height > maxHeight) {
      const ratio = Math.min(maxWidth / width, maxHeight / height);
      width = Math.round(width * ratio);
      height = Math.round(height * ratio);
    }

    // Se não precisa redimensionar, comprimir diretamente
    if (width === canvas.width && height === canvas.height) {
      const tryCompress = (currentQuality) => {
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Falha ao comprimir imagem'));
              return;
            }

            const sizeKB = blob.size / 1024;
            console.log(`[ImageCompression] Imagem comprimida: ${sizeKB.toFixed(2)}KB (qualidade: ${currentQuality.toFixed(2)})`);

            if (sizeKB <= maxSizeKB || currentQuality <= 0.5) {
              resolve(blob);
            } else {
              const newQuality = Math.max(0.5, currentQuality - 0.1);
              console.log(`[ImageCompression] Tamanho ainda grande (${sizeKB.toFixed(2)}KB), tentando qualidade ${newQuality.toFixed(2)}`);
              tryCompress(newQuality);
            }
          },
          'image/jpeg',
          currentQuality
        );
      };

      tryCompress(quality);
      return;
    }

    // Criar novo canvas com dimensões redimensionadas
    const resizedCanvas = document.createElement('canvas');
    resizedCanvas.width = width;
    resizedCanvas.height = height;

    const ctx = resizedCanvas.getContext('2d');
    
    // Melhorar qualidade de renderização
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    
    // Desenhar imagem redimensionada
    ctx.drawImage(canvas, 0, 0, width, height);

    // Comprimir
    const tryCompress = (currentQuality) => {
      resizedCanvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error('Falha ao comprimir imagem'));
            return;
          }

          const sizeKB = blob.size / 1024;
          console.log(`[ImageCompression] Imagem redimensionada e comprimida: ${sizeKB.toFixed(2)}KB (${width}x${height}, qualidade: ${currentQuality.toFixed(2)})`);

          if (sizeKB <= maxSizeKB || currentQuality <= 0.5) {
            resolve(blob);
          } else {
            const newQuality = Math.max(0.5, currentQuality - 0.1);
            console.log(`[ImageCompression] Tamanho ainda grande (${sizeKB.toFixed(2)}KB), tentando qualidade ${newQuality.toFixed(2)}`);
            tryCompress(newQuality);
          }
        },
        'image/jpeg',
        currentQuality
      );
    };

    tryCompress(quality);
  });
}
